import { useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import DrawableCheckMark from '../../../components/SVGIcons/DrawableCheckMark';
import { LAST_DAY_NUMBER } from '../../../config/constants';
import type { BonusItem } from '../../../dto/bonus.types';

import classes from './BonusCards.module.scss';
import WinAmount from './WinAmount';
import WinImage from './WinImage';

export default function BonusCard(props: {
  handleBonusDayClaim: () => Promise<void>;
  data: BonusItem;
  claimed?: boolean;
  isClaiming: boolean;
  isEntitledToBonus?: boolean;
}) {
  const { isEntitledToBonus, claimed, isClaiming, handleBonusDayClaim } = props;
  const { day, gc_amount, sc_amount, image_url, image_url_2, image_url_3 } = props.data;
  const { t } = useTranslation();
  // animation-popup
  const [showAnimation, setShowAnimation] = useState(true);
  // animation-press-bounce
  const [showBounceAnimation, setShowBounceAnimation] = useState(false);

  const isLastDay = day === LAST_DAY_NUMBER;

  const rootClassName = classNames(classes.bonusCard, {
    [classes.claimed]: claimed || isEntitledToBonus,
    [classes.last]: isLastDay,
    [classes.zIndexOne]: isEntitledToBonus,
    [classes.disabled]: showBounceAnimation || isClaiming,
    ['animation-popup']: showAnimation,
    ['animation-press-bounce']: showBounceAnimation,
  });

  const contentClassName = classNames(classes.content, {
    [classes.contentClaimed]: claimed,
  });

  const onClickBonusCard = () => {
    setShowBounceAnimation(true);
  };

  const onBonusCardAnimationEnd = () => {
    setShowAnimation(false);
    if (showBounceAnimation) {
      setShowBounceAnimation(false);
      void handleBonusDayClaim();
    }
  };
  const isCoins = gc_amount > 0 || sc_amount > 0;

  return (
    <div
      className={rootClassName}
      data-day={day}
      data-testid={`bonus-card-${day}`}
      onAnimationEnd={onBonusCardAnimationEnd}
      onClick={onClickBonusCard}
    >
      <div className={classes.dayNumber} data-testid={`bonus-card-${day}-label`}>
        {t('bonus.day', { day })}
      </div>
      <div className={classNames(contentClassName, !isCoins ? classes.onlyImage : '')}>
        {!isLastDay ? (
          <div className={classNames(classes.bonus)}>
            <div className={classes.cardHeader}>
              {sc_amount > 0 ? (
                <>
                  {gc_amount > 0 && (
                    <>
                      <WinAmount gcAmount={gc_amount} scAmount={0} />
                      <div className={classes.plus}>+</div>
                    </>
                  )}

                  <WinAmount gcAmount={0} scAmount={sc_amount} />
                </>
              ) : gc_amount > 0 ? (
                <WinAmount gcAmount={gc_amount} scAmount={0} />
              ) : null}
            </div>
            <WinImage day={day} imageUrl={image_url} small={isCoins} />
          </div>
        ) : null}
        {isLastDay ? (
          <div className={classNames(classes.lastBonusDay)}>
            {gc_amount > 0 ? (
              <div className={classes.reward}>
                <WinAmount gcAmount={gc_amount} scAmount={0} />
                <WinImage day={day} imageUrl={image_url} />
              </div>
            ) : null}
            {gc_amount > 0 && sc_amount > 0 ? <div className={classes.plusSign}> + </div> : null}
            {sc_amount > 0 ? (
              <div className={classes.reward}>
                <WinAmount gcAmount={0} scAmount={sc_amount} />
                <WinImage day={day} imageUrl={image_url_2} />
              </div>
            ) : null}
            {(sc_amount > 0 || gc_amount > 0) && image_url_3 ? <div className={classes.plusSign}> + </div> : null}
            {image_url_3 ? (
              <div className={classNames(classes.reward, classes.frb)}>
                <WinImage day={day} imageUrl={image_url_3} />
              </div>
            ) : null}
          </div>
        ) : null}

        {claimed && (
          <div className={classes.checkMarkWrapper}>
            <DrawableCheckMark className={classes.checkMark} />
          </div>
        )}

        {isEntitledToBonus && <div className={classNames(classes.checkMark, classes.noBg)} />}
      </div>
    </div>
  );
}
